import { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  type ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

import { Button } from '~/DesignSystem/components/button'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '~/DesignSystem/components/carousel'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '~/DesignSystem/components/dialog'
import { cn } from '~/DesignSystem/lib/utils'

type ProductVariantCarouselProps = React.ComponentProps<'div'> & {
  imageUrls: string[]
}

const ProductVariantCarousel: React.FC<ProductVariantCarouselProps> = ({
  imageUrls,
  className,
  ...props
}) => {
  const mainApiRef = useRef<CarouselApi>(undefined)
  const thumbsApiRef = useRef<CarouselApi>(undefined)
  const contentDialogRef = useRef<HTMLDivElement>(null)
  const imgDialogRef = useRef<HTMLImageElement>(null)
  const zoomRef = useRef<ReactZoomPanPinchContentRef>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedImg, setSelectedImg] = useState<string>()
  const [minScale, setMinScale] = useState(1)

  const onSelect = useCallback(() => {
    const mainApi = mainApiRef.current

    if (!mainApi) return

    const index = mainApi.selectedScrollSnap()

    setSelectedIndex(index)
    thumbsApiRef.current?.scrollTo(index)
  }, [])

  const setMainApi = useCallback(
    (api: CarouselApi) => {
      mainApiRef.current?.off('select', onSelect)
      mainApiRef.current?.off('reInit', onSelect)

      mainApiRef.current = api
      onSelect()
      mainApiRef.current?.on('select', onSelect)
      mainApiRef.current?.on('reInit', onSelect)
    },
    [onSelect],
  )

  const setThumbsApi = useCallback(
    (api: CarouselApi) => {
      thumbsApiRef.current = api
      onSelect()
    },
    [onSelect],
  )

  const setZoomScale = useCallback(() => {
    const imgElement = imgDialogRef.current
    const contentDialogElement = contentDialogRef.current

    if (!imgElement || !contentDialogElement) return

    const { width: imgWidth, height: imgHeight } = imgElement.getBoundingClientRect()
    const { width: elementWidth, height: elementHeight } =
      contentDialogElement.getBoundingClientRect()

    const widthScale = elementWidth / imgWidth
    const heightScale = elementHeight / imgHeight
    const minScale = Math.min(widthScale, heightScale)
    const maxScale = Math.max(widthScale, heightScale)

    zoomRef.current?.centerView(maxScale)
    setMinScale(minScale)
  }, [])

  useEffect(() => {
    let timeoutId: number

    const updateZoomScale = () => {
      zoomRef.current?.centerView(1)
      timeoutId = window.setTimeout(() => {
        setZoomScale()
      }, 600)
    }

    window.addEventListener('resize', updateZoomScale)
    return () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener('resize', updateZoomScale)
    }
  }, [setZoomScale])

  if (!imageUrls.length) {
    return null
  }

  return (
    <div className={cn('flex flex-col gap-2', className)} {...props}>
      <Carousel setApi={setMainApi}>
        <CarouselContent>
          {imageUrls.map(imageUrl => {
            return (
              <CarouselItem key={imageUrl}>
                <button
                  type="button"
                  onClick={() => setSelectedImg(imageUrl)}
                  className="px-4 block w-full cursor-zoom-in"
                >
                  <img className="w-full aspect-2/1 object-cover" src={imageUrl} alt="" />
                </button>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        {imageUrls.length > 1 ? <CarouselPrevious className="-left-2" variant="ghost" /> : null}
        {imageUrls.length > 1 ? <CarouselNext className="-right-2" variant="ghost" /> : null}
      </Carousel>
      <Carousel className="max-w-full mx-auto" setApi={setThumbsApi}>
        <CarouselContent>
          {imageUrls.map((imageUrl, index) => {
            const isSelected = selectedIndex === index

            return (
              <CarouselItem key={`${imageUrl}-thumb`} className="basis-20">
                <button
                  type="button"
                  onClick={() => mainApiRef.current?.scrollTo(index)}
                  className={`block ${isSelected ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={isSelected}
                >
                  <img src={imageUrl} alt="" className="h-16 aspect-square object-cover" />
                </button>
              </CarouselItem>
            )
          })}
        </CarouselContent>
      </Carousel>

      <Dialog open={!!selectedImg} onOpenChange={open => !open && setSelectedImg(undefined)}>
        <DialogContent
          showCloseButton
          className="rounded-none w-dvw h-dvh max-w-dvw sm:max-w-dvw flex flex-col p-4"
        >
          <DialogTitle className="sr-only" />
          <DialogDescription className="sr-only" />
          <div className="flex flex-1 -mt-4 -mx-4 min-h-0 overflow-hidden" ref={contentDialogRef}>
            <TransformWrapper centerOnInit centerZoomedOut ref={zoomRef} minScale={minScale}>
              <TransformComponent wrapperClass="h-full! w-full!">
                <img src={selectedImg} ref={imgDialogRef} onLoad={setZoomScale} />
              </TransformComponent>
            </TransformWrapper>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                <FormattedMessage id="modal.actions.close" />
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ProductVariantCarousel
